<template>
  <div>
    <div class="header-section">
      <div class="content-section">
        <h3 class="section-title">
          {{ $t("OrderStatusSettings") }}
        </h3>

        <!-- Order Status Notification Settings -->
        <div class="content mb-5">
          <div class="flex justify-between items-center mb-4">
            <button @click="openAddRowModal" class="BTNMAIN py-3 px-6 flex gap-2 items-center">
              <i class="icon-smsir-plus text-[20px]"></i>
              <span class="text-[#0D0D0D] text-base">{{ $t("createNewRow") }}</span>
            </button>
          </div>

          <!-- Loading Spinner -->
          <LoadingSpinner v-if="loading" />

          <!-- No rows message -->
          <div
            v-else-if="notificationRows.length === 0"
            class="no-templates p-4 text-center bg-gray-50 rounded-lg"
          >
            <p class="text-gray-500">
              {{ $t("noOrderStatusRowsYet") }}
            </p>
            <p class="text-gray-500 text-sm mt-2">
              {{ $t("clickCreateNewRowToAdd") }}
            </p>
          </div>

          <!-- Notification Rows Table -->
          <div v-else class="overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-100">
                  <th class="p-3 text-right w-1/3 text-[#4D4D4D] text-sm font-light">نوع وضعیت سفارش</th>
                  <th class="p-3 text-right w-1/3 text-[#4D4D4D] text-sm font-light">ارسال به</th>
                  <th class="p-3 text-right w-1/3 text-[#4D4D4D] text-sm font-light">عملیات</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in notificationRows" :key="index" class="border-b hover:bg-gray-50">
                  <td class="p-3 text-base text-[#0D0D0D]">{{ getOrderStatusText(row.order_status) }}</td>
                  <td class="p-3 text-base text-[#0D0D0D]">{{ getRecipientText(row.recipient_type) }}</td>
                  <td class="p-3">
                    <div class="flex gap-2">
                      <button @click="editRow(index)" class="text-[#0D0D0D] hover:text-blue-600">
                        <i class="icon-smsir-write text-3xl"></i>
                      </button>
                      <button @click="deleteNotification(index)" class="text-[#0D0D0D] hover:text-red-600">
                        <i class="icon-smsir-trash-bin-trash text-2xl"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Admin Configuration Modal -->
    <div v-if="showAdminModal" class="modal">
      <div class="modal-content max-w-[900px]">
        <div class="modal-header">
          <h2 class="text-xl font-bold">{{ $t('configureNotification') }}</h2>
          <button @click="closeModal" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <!-- First row: Recipient Type selection -->
          <div class="mb-6">
            <div class="flex flex-wrap gap-4">
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('selectOrderStatus') }}</label>
                <select v-model="currentRowOrder.order_status" class="input-main w-full">
                  <option v-for="status in orderStatuses" :key="status.value" :value="status.value">
                    {{ status.label }}
                  </option>
                </select>
              </div>
              
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('sendTo') }}</label>
                <div class="flex gap-4 mt-2 py-3">
                  <label class="flex items-center">
                    <input type="checkbox" v-model="editRecipientTypes" value="admin" class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]">
                    <span>{{ $t('admin') }}</span>
                  </label>
                  <label class="flex items-center">
                    <input type="checkbox" v-model="editRecipientTypes" value="customer" class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]">
                    <span>{{ $t('customer') }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Admin Configuration -->
          <div v-if="editRecipientTypes.includes('admin')" class="mb-6 p-4 border border-gray-200 rounded-lg">
            <h3 class="text-lg font-medium mb-3">{{ $t('adminConfiguration') }}</h3>
            
            <!-- Second row: Template ID and Admin Mobile Numbers -->
            <div class="flex flex-wrap gap-4 mb-4">
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
                <input
                  v-model="currentRowConfig.admin_template_id"
                  type="number"
                  class="input-main w-full"
                  :placeholder="$t('templateId')"
                />
              </div>
              
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('adminMobileNumbers') }}</label>
                <input
                  v-model="currentRowConfig.admin_mobile_numbers"
                  type="text"
                  class="input-main w-full"
                  :placeholder="$t('enterCommaSeperatedNumbers')"
                />
                <small class="text-gray-500">{{ $t('commaSeperatedNumbers') }}</small>
              </div>
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
              <div class="relative">
                <input
                  v-model="currentRowConfig.admin_message"
                  type="text"
                  class="input-main w-full"
                  :placeholder="$t('enterVariables')"
                />
                
                <div class="mt-2">
                  <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
                  <div class="flex flex-wrap gap-2">
                    <button 
                      v-for="param in orderParameters" 
                      :key="param.id"
                      @click="insertParameter(param.placeholder, 'admin_message')"
                      class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                    >
                      {{ param.label }} ({{ param.placeholder }})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Customer Configuration -->
          <div v-if="editRecipientTypes.includes('customer')" class="mb-6 p-4 border border-gray-200 rounded-lg">
            <h3 class="text-lg font-medium mb-3">{{ $t('customerConfiguration') }}</h3>
            
            <div class="mb-4">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
              <input
                v-model="currentRowConfig.customer_template_id"
                type="number"
                class="input-main w-full"
                :placeholder="$t('templateId')"
              />
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
              <div class="relative">
                <input
                  v-model="currentRowConfig.customer_message"
                  type="text"
                  class="input-main w-full"
                  :placeholder="$t('enterVariables')"
                />
                
                <div class="mt-2">
                  <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
                  <div class="flex flex-wrap gap-2">
                    <button 
                      v-for="param in orderParameters" 
                      :key="param.id"
                      @click="insertParameter(param.placeholder, 'customer_message')"
                      class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                    >
                      {{ param.label }} ({{ param.placeholder }})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="editRecipientTypes.length === 0" class="p-4 bg-yellow-50 rounded-lg text-yellow-700 text-center">
            {{ $t('pleaseSelectRecipientType') }}
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeModal" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
            {{ $t('cancel') }}
          </button>
          <button @click="saveRowConfig" class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center" :disabled="editRecipientTypes.length === 0">
            <i class="icon-smsir-save text-xl text-white"></i>
            <span class="text-white text-base">
              {{ $t('saveSettings') }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Customer Configuration Modal -->
    <div v-if="showCustomerModal" class="modal">
      <div class="modal-content max-w-3xl">
        <div class="modal-header">
          <h2 class="text-xl font-bold">{{ $t('configureCustomerNotification') }}</h2>
          <button @click="closeModal" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <div class="mb-4">
            <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
            <input
              v-model="currentRowConfig.admin_template_id"
              type="number"
              class="input-main w-full"
              :placeholder="$t('templateId')"
            />
          </div>
          
          <div class="mb-4">
            <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
            <div class="relative">
              <input
                v-model="currentRowConfig.customer_message"
                type="text"
                class="input-main w-full"
                :placeholder="$t('enterVariables')"
              />
              
              <div class="mt-2">
                <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
                <div class="flex flex-wrap gap-2">
                  <button 
                    v-for="param in orderParameters" 
                    :key="param.id"
                    @click="insertParameter(param.placeholder, 'customer_message')"
                    class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                  >
                    {{ param.label }} ({{ param.placeholder }})
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeModal" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
            {{ $t('cancel') }}
          </button>
          <button @click="saveRowConfig" class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center">
            <i class="icon-smsir-save text-xl text-white"></i>
            <span class="text-white text-base">
              {{ $t('saveSettings') }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConfirmation" class="modal">
      <div class="modal-content max-w-md">
        <div class="modal-header">
          <h2 class="text-[18px] text-[#0D0D0D]">{{ $t('confirmDelete') }}</h2>
          <button @click="showDeleteConfirmation = false" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <p class="mb-4 text-[#4D4D4D]">{{ $t('deleteRowConfirmation') }}</p>
        </div>
        
        <div class="modal-footer">
          <button @click="showDeleteConfirmation = false" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
            {{ $t('cancel') }}
          </button>
          <button @click="confirmDeleteRow" class="px-4 py-2 bg-red-500 text-white rounded-full flex items-center gap-2 text-base">
            <i class="icon-smsir-trash-bin-trash text-xl text-white"></i>
            {{ $t('remove') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add New Row Modal -->
    <div v-if="showAddRowModal" class="modal">
      <div class="modal-content max-w-[900px]">
        <div class="modal-header">
          <h2 class="text-xl font-bold">{{ $t('addNewStatusOrder') }}</h2>
          <button @click="closeAddRowModal" class="text-2xl">&times;</button>
        </div>
        
        <div class="modal-body">
          <!-- First row: Status and Recipient Type -->
          <div class="flex flex-wrap gap-4 mb-6">
            <div class="flex-1 min-w-[250px]">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('selectOrderStatus') }}</label>
              <select v-model="newRow.order_status" class="input-main w-full">
                <option value="">{{ $t('selectStatus') }}</option>
                <option v-for="status in orderStatuses" :key="status.value" :value="status.value">
                  {{ status.label }}
                </option>
              </select>
            </div>
            
            <div class="flex-1 min-w-[250px]">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('sendTo') }}</label>
              <div class="flex gap-4 mt-2 py-3">
                <label class="flex items-center">
                  <input type="checkbox" v-model="newRow.recipient_type" value="admin" class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]">
                  <span>{{ $t('admin') }}</span>
                </label>
                <label class="flex items-center">
                  <input type="checkbox" v-model="newRow.recipient_type" value="customer" class="ml-2 border border-[#D9D9D9] rounded-[7px] size-[20px]">
                  <span>{{ $t('customer') }}</span>
                </label>
              </div>
            </div>
          </div>
          
          <!-- Admin Configuration -->
          <div v-if="newRow.recipient_type.includes('admin')" class="mb-6 p-4 border border-gray-200 rounded-lg">
            <h3 class="text-lg font-medium mb-3">{{ $t('adminConfiguration') }}</h3>
            
            <!-- Second row: Template ID and Admin Mobile Numbers -->
            <div class="flex flex-wrap gap-4 mb-4">
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
                <input
                  v-model="newRow.config.admin_template_id"
                  type="number"
                  class="input-main w-full"
                  :placeholder="$t('templateId')"
                />
              </div>
              
              <div class="flex-1 min-w-[250px]">
                <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('adminMobileNumbers') }}</label>
                <input
                  v-model="newRow.config.admin_mobile_numbers"
                  type="text"
                  class="input-main w-full"
                  :placeholder="$t('enterCommaSeperatedNumbers')"
                />
                <small class="text-gray-500">{{ $t('commaSeperatedNumbers') }}</small>
              </div>
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
              <div class="relative">
                <input
                  v-model="newRow.config.admin_message"
                  type="text"
                  class="input-main w-full"
                  :placeholder="$t('enterVariables')"
                />
                
                <div class="mt-2">
                  <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
                  <div class="flex flex-wrap gap-2">
                    <button 
                      v-for="param in orderParameters" 
                      :key="param.id"
                      @click="insertParameterNewRow(param.placeholder, 'admin_message')"
                      class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                    >
                      {{ param.label }} ({{ param.placeholder }})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Customer Configuration -->
          <div v-if="newRow.recipient_type.includes('customer')" class="mb-6 p-4 border border-gray-200 rounded-lg">
            <h3 class="text-lg font-medium mb-3">{{ $t('customerConfiguration') }}</h3>
            
            <div class="mb-4">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('templateId') }}</label>
              <input
                v-model="newRow.config.customer_template_id"
                type="number"
                class="input-main w-full"
                :placeholder="$t('templateId')"
              />
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-normal text-[#4D4D4D] mb-1">{{ $t('messageTemplate') }}</label>
              <div class="relative">
                <input
                  v-model="newRow.config.customer_message"
                  type="text"
                  class="input-main w-full"
                  :placeholder="$t('enterVariables')"
                />
                
                <div class="mt-2">
                  <div class="text-sm text-gray-700 mb-1 w-full">{{ $t('availableParameters') }}:</div>
                  <div class="flex flex-wrap gap-2">
                    <button 
                      v-for="param in orderParameters" 
                      :key="param.id"
                      @click="insertParameterNewRow(param.placeholder, 'customer_message')"
                      class="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
                    >
                      {{ param.label }} ({{ param.placeholder }})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeAddRowModal" class="px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 text-base">
            {{ $t('cancel') }}
          </button>
          <button 
            @click="addNewRow" 
            class="BTNMAIN bg-[#12BA6C] flex gap-2 items-center"
            :disabled="!isValidNewRow"
          >
            <i class="icon-smsir-save text-xl text-white"></i>
            <span class="text-white text-base">
              {{ $t('addStatus') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import LoadingSpinner from '../LoadingSpinner.vue';

export default {
  name: "WooCommerceSettings",
  components: {
    LoadingSpinner
  },
  data() {
    return {
      loading: true,
      notificationRows: [],
      orderStatuses: [],
      orderParameters: [
        { id: 1, label: this.$t('orderID'), placeholder: "#OrderId#" },
        { id: 2, label: this.$t('orderTotal'), placeholder: "#OrderTotal#" },
        { id: 3, label: this.$t('customerName'), placeholder: "#CustomerName#" },
        { id: 4, label: this.$t('customerFirstName'), placeholder:"#CustomerFirstName#" },
        { id: 5, label: this.$t('customerLastName'), placeholder: "#CustomerLastName#" },
        { id: 6, label: this.$t('orderDate'), placeholder: "#OrderDate#" },
        { id: 7, label: this.$t('paymentMethod'), placeholder: "#PaymentMethod#" },
        { id: 8, label: this.$t('shippingMethod'), placeholder: "#ShippingMethod#" },
        { id: 9, label: this.$t('orderItems'), placeholder: "#OrderItems#" }
      ],
      showAdminModal: false,
      showCustomerModal: false,
      showDeleteConfirmation: false,
      showAddRowModal: false,
      currentRowIndex: null,
      rowToDelete: null,
      editRecipientTypes: [],
      currentRowConfig: {
        admin_mobile_numbers: '',
        admin_template_id: '',
        admin_message: '',
        customer_template_id: '',
        customer_message: ''
      },
      saving: false,
      newRow: {
        order_status: '',
        recipient_type: [],
        config: {
          admin_mobile_numbers: '',
          admin_template_id: '',
          admin_message: '',
          customer_template_id: '',
          customer_message: ''
        }
      },
      currentRowOrder: {
        order_status: '',
      },
    };
  },
  computed: {
    isValidNewRow() {
      // Only require order status and recipient type selection
      const hasOrderStatus = this.newRow.order_status;
      const hasRecipients = this.newRow.recipient_type.length > 0;
      
      return hasOrderStatus && hasRecipients;
    },
    isBothRecipientTypes() {
      if (this.currentRowIndex === null) return false;
      
      const row = this.notificationRows[this.currentRowIndex];
      if (!row) return false;
      
      // Check if recipient_type is an array (new format)
      if (Array.isArray(row.recipient_type)) {
        return row.recipient_type.includes('admin') && row.recipient_type.includes('customer');
      }
      
      // Handle string format for backward compatibility (unlikely but possible)
      return row.recipient_type === 'both';
    }
  },
  mounted() {
    this.loadSettings();
    this.fetchOrderStatuses();
  },
  created() {
  },
  methods: {
    async fetchOrderStatuses() {
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_wc_order_statuses",
            nonce: window.sms_ir_login.nonce
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (response.data.success) {
          this.orderStatuses = response.data.data;
        } else {
          console.error("Failed to load order statuses:", response.data.data);
          // Fallback to default statuses if fetch fails
          this.setDefaultOrderStatuses();
        }
      } catch (error) {
        console.error("Error loading order statuses:", error);
        // Fallback to default statuses if fetch fails
        this.setDefaultOrderStatuses();
      }
    },
    
    setDefaultOrderStatuses() {
      // Fallback to default statuses if API call fails
      this.orderStatuses = [
        { value: "pending", label: this.$t('statusPending') },
        { value: "processing", label: this.$t('statusProcessing') },
        { value: "on-hold", label: this.$t('statusOnHold') },
        { value: "completed", label: this.$t('statusCompleted') },
        { value: "cancelled", label: this.$t('statusCancelled') },
        { value: "refunded", label: this.$t('statusRefunded') },
        { value: "failed", label: this.$t('statusFailed') }
      ];
    },

    async loadSettings() {
      this.loading = true;
      try {
        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_get_wc_notification_rows",
            nonce: window.sms_ir_login.nonce
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (response.data.success) {
          const rows = response.data.data;
          this.notificationRows = Array.isArray(rows) ? rows : [];
        } else {
          console.error("Failed to load settings:", response.data.data);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
        Swal.fire({
          text: this.$t("failedToLoadSettings"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal"
          }
        });
      } finally {
        this.loading = false;
      }
    },

    openAddRowModal() {
      this.closeModal(); // Close any other open modals first
      this.showAddRowModal = true;
      // Reset newRow with empty config
      this.newRow = {
        order_status: '',
        recipient_type: [],
        config: {
          admin_mobile_numbers: '',
          admin_template_id: '',
          admin_message: '',
          customer_template_id: '',
          customer_message: ''
        }
      };
    },

    closeAddRowModal() {
      this.showAddRowModal = false;
      // Reset newRow with empty config
      this.newRow = {
        order_status: '',
        recipient_type: [],
        config: {
          admin_mobile_numbers: '',
          admin_template_id: '',
          admin_message: '',
          customer_template_id: '',
          customer_message: ''
        }
      };
    },

    async addNewRow() {
      if (!this.isValidNewRow) {
        return;
      }

      // Prepare the config object based on recipient types
      let config = {};
      const hasAdmin = this.newRow.recipient_type.includes('admin');
      const hasCustomer = this.newRow.recipient_type.includes('customer');

      if (hasAdmin && hasCustomer) {
        // Both recipient types
        config = {
          admin_mobile_numbers: this.newRow.config.admin_mobile_numbers || '',
          admin_template_id: this.newRow.config.admin_template_id || '',
          admin_message: this.newRow.config.admin_message || '',
          customer_template_id: this.newRow.config.customer_template_id || '',
          customer_message: this.newRow.config.customer_message || ''
        };
      } else if (hasAdmin) {
        // Admin only - Fix admin fields mapping
        config = {
          admin_mobile_numbers: this.newRow.config.admin_mobile_numbers || '',
          admin_template_id: this.newRow.config.admin_template_id || '',
          admin_message: this.newRow.config.admin_message || '',
          customer_template_id: '',
          customer_message: ''
        };
      } else if (hasCustomer) {
        // Customer only
        config = {
          admin_mobile_numbers: '',
          admin_template_id: '',
          admin_message: '',
          customer_template_id: this.newRow.config.customer_template_id || '',
          customer_message: this.newRow.config.customer_message || ''
        };
      }

      // Add the new row with consistent field names
      this.notificationRows.push({
        order_status: this.newRow.order_status,
        recipient_type: this.newRow.recipient_type,
        config: {
          admin_mobile_numbers: config.admin_mobile_numbers,
          admin_template_id: config.admin_template_id,
          admin_message: config.admin_message,
          customer_template_id: config.customer_template_id,
          customer_message: config.customer_message
        }
      });

      // Close the modal
      this.closeAddRowModal();
      
      // Save settings automatically
      await this.saveSettings();
    },

    deleteRow(index) {
      this.closeModal(); // Close any other open modals first
      this.rowToDelete = index;
      this.showDeleteConfirmation = true;
    },

    async confirmDeleteRow() {
      if (this.rowToDelete !== null) {
        this.notificationRows.splice(this.rowToDelete, 1);
        this.rowToDelete = null;
        this.showDeleteConfirmation = false;
        
        // Save settings automatically
        await this.saveSettings();
      }
    },

    recipientTypeChanged(index) {
      const row = this.notificationRows[index];
      
      if (!row.recipient_type) {
        return;
      }
      
      if (!row.order_status) {
        row.recipient_type = "";
        
        Swal.fire({
          text: this.$t("selectOrderStatus"),
          icon: "warning",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal"
          }
        });
        return;
      }
      
      // Automatically open the configuration modal based on recipient type
      this.configureRow(index);
    },

    configureRow(index) {
      
      this.closeModal(); // Close any other open modals first
      const row = this.notificationRows[index];
      
      if (!row) {
        console.error('Row not found for index:', index);
        return;
      }
      
      if (!row.order_status || !row.recipient_type) {
        console.error('Row missing order_status or recipient_type:', row);
        return;
      }

      this.currentRowIndex = index;
      
      if (!row.config) {
        row.config = {};
      }

      this.currentRowConfig = {
        admin_mobile_numbers: row.config.admin_mobile_numbers || '',
        admin_template_id: row.config.admin_template_id || '',
        admin_message: row.config.admin_message || '',
        customer_template_id: row.config.customer_template_id || '',
        customer_message: row.config.customer_message || ''
      };

      // Set the order status
      this.currentRowOrder = {
        order_status: row.order_status || '',
      };

      // Check if this is a combined notification (both admin and customer)
      const hasBothTypes = Array.isArray(row.recipient_type) && 
                          row.recipient_type.includes('admin') && 
                          row.recipient_type.includes('customer');

      if (hasBothTypes) {
        this.showAdminModal = true;
        return;
      }

      if (Array.isArray(row.recipient_type)) {
        if (row.recipient_type.includes('admin')) {
          this.showAdminModal = true;
        } else if (row.recipient_type.includes('customer')) {
          this.showCustomerModal = true;
        }
      } else {
        if (row.recipient_type === "admin") {
          this.showAdminModal = true;
        } else if (row.recipient_type === "customer") {
          this.showCustomerModal = true;
        }
      }
    },

    async saveRowConfig() {
      if (this.currentRowIndex === null || this.editRecipientTypes.length === 0) return;

      const row = this.notificationRows[this.currentRowIndex];
      
      // Update order status
      row.order_status = this.currentRowOrder.order_status;
      
      // Update recipient type based on checkboxes
      row.recipient_type = [...this.editRecipientTypes];
      
      // Create a new config object
      let newConfig = {};
      
      if (this.editRecipientTypes.includes('admin')) {
        newConfig.admin_mobile_numbers = this.currentRowConfig.admin_mobile_numbers;
        newConfig.admin_template_id = this.currentRowConfig.admin_template_id; // Changed name
        newConfig.admin_message = this.currentRowConfig.admin_message; // Changed name
      }
      
      if (this.editRecipientTypes.includes('customer')) {
        newConfig.customer_template_id = this.currentRowConfig.customer_template_id || '';
        newConfig.customer_message = this.currentRowConfig.customer_message || '';
      }
      
      // Update row config
      row.config = newConfig;
      
      // Close the modal
      this.closeModal();
      
      // Save settings automatically
      await this.saveSettings();
    },

    closeModal() {
      // Close all modals
      this.showAdminModal = false;
      this.showCustomerModal = false;
      this.showDeleteConfirmation = false;
      this.showAddRowModal = false;
      
      // Reset all modal-related data
      this.currentRowIndex = null;
      this.editRecipientTypes = [];
      this.currentRowConfig = {
        admin_mobile_numbers: '',
        admin_template_id: '',
        admin_message: '',
        customer_template_id: '',
        customer_message: ''
      };
      this.currentRowOrder = {
        order_status: '',
      };
      this.newRow = {
        order_status: '',
        recipient_type: [],
        config: {
          admin_mobile_numbers: '',
          admin_template_id: '',
          admin_message: '',
          customer_template_id: '',
          customer_message: ''
        }
      };
    },

    insertParameter(parameter, targetField) {
      // Simply append the parameter to the end of the current value
      this.currentRowConfig[targetField] = (this.currentRowConfig[targetField] || '') + parameter;
      
      // Focus on the textarea after inserting if possible
      this.$nextTick(() => {
        const textarea = document.querySelector(`textarea[v-model="currentRowConfig.${targetField}"]`);
        if (textarea) {
          textarea.focus();
          textarea.scrollTop = textarea.scrollHeight;
        }
      });
    },

    insertParameterNewRow(parameter, targetField) {
      // For the add new row modal
      if (targetField === 'customer_message') {
        this.newRow.config.customer_message = (this.newRow.config.customer_message || '') + parameter;
      } else {
        this.newRow.config.admin_message = (this.newRow.config.admin_message || '') + parameter;
      }
      
      // Focus on the textarea after inserting if possible
      this.$nextTick(() => {
        const selector = targetField === 'customer_message' 
          ? `textarea[v-model="newRow.config.customer_message"]` 
          : `textarea[v-model="newRow.config.admin_message"]`;
        
        const textarea = document.querySelector(selector);
        if (textarea) {
          textarea.focus();
          textarea.scrollTop = textarea.scrollHeight;
        }
      });
    },

    async saveSettings() {
      this.saving = true;

      try {
        // Format the notification rows to include parameters
        const formattedRows = this.notificationRows.map(row => {
          const formattedRow = { ...row };
          
          // Format admin message parameters
          if (formattedRow.config.admin_message) {
            // Extract parameters from the message template with # format
            const matches = formattedRow.config.admin_message.match(/#([^#]+)#/g) || [];
            const params = {};
            
            matches.forEach(match => {
              const paramName = match.slice(1, -1); // Remove # and #
              params[paramName] = paramName; // Store the parameter name as the value
            });
            
            // Only set admin_params if we found parameters
            if (Object.keys(params).length > 0) {
              formattedRow.config.admin_params = params;
            }
          }
          
          // Format customer message parameters
          if (formattedRow.config.customer_message) {
            // Extract parameters from the customer message template with # format
            const matches = formattedRow.config.customer_message.match(/#([^#]+)#/g) || [];
            const params = {};
            
            matches.forEach(match => {
              const paramName = match.slice(1, -1); // Remove # and #
              params[paramName] = paramName; // Store the parameter name as the value
            });
            
            // Only set customer_params if we found parameters
            if (Object.keys(params).length > 0) {
              formattedRow.config.customer_params = params;
            }
          }
          
          return formattedRow;
        });



        const response = await axios.post(
          window.sms_ir_login.ajaxUrl,
          new URLSearchParams({
            action: "sms_ir_save_wc_notification_rows",
            nonce: window.sms_ir_login.nonce,
            rows: JSON.stringify(formattedRows)
          }),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            }
          }
        );

        if (response.data.success) {
          Swal.fire({
            text: this.$t("SettingsSavedSuccessfully"),
            icon: "success",
            toast: true,
            position: "top-start",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: "smsir-swal"
            }
          });
        } else {
          throw new Error(response.data.data || this.$t("SettingsSaveError"));
        }
      } catch (error) {
        console.error("Error saving settings:", error);
        Swal.fire({
          text: this.$t("SettingsSaveError"),
          icon: "error",
          toast: true,
          position: "top-start",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: "smsir-swal"
          }
        });
      } finally {
        this.saving = false;
      }
    },

    getOrderStatusText(status) {
      const statusObject = this.orderStatuses.find(s => s.value === status);
      return statusObject ? statusObject.label : status;
    },

    getRecipientText(recipientType) {
      if (Array.isArray(recipientType)) {
        if (recipientType.includes('admin') && recipientType.includes('customer')) {
          return 'مدیر و مشتری';
        } else if (recipientType.includes('admin')) {
          return 'مدیر';
        } else if (recipientType.includes('customer')) {
          return 'مشتری';
        }
      }
      return recipientType === 'admin' ? 'مدیر' : 'مشتری';
    },

    configureNotification(index) {
      this.configureRow(index);
    },

    deleteNotification(index) {
      this.deleteRow(index);
    },
    
    editRow(index) {
      
      const row = this.notificationRows[index];
      
      if (!row) {
        console.error("Row not found");
        return;
      }
      
      this.closeModal();
      
      this.currentRowIndex = index;
      
      // Extract config data properly
      const config = row.config || {};
      
      this.currentRowConfig = {
        admin_mobile_numbers: config.admin_mobile_numbers || '',
        admin_template_id: config.admin_template_id || '', // Changed from admin_template_id
        admin_message: config.admin_message || '', // Changed from admin_message
        customer_template_id: config.customer_template_id || '',
        customer_message: config.customer_message || ''
      };
      
      // Set the order status
      this.currentRowOrder = {
        order_status: row.order_status || '',
      };
      
      this.editRecipientTypes = [];
      if (Array.isArray(row.recipient_type)) {
        this.editRecipientTypes = [...row.recipient_type];
      } else if (typeof row.recipient_type === 'string') {
        if (row.recipient_type === 'admin' || row.recipient_type === 'both') {
          this.editRecipientTypes.push('admin');
        }
        if (row.recipient_type === 'customer' || row.recipient_type === 'both') {
          this.editRecipientTypes.push('customer');
        }
      }

      
      this.showAdminModal = true;
    },

    async saveRowConfig() {
      if (this.currentRowIndex === null || this.editRecipientTypes.length === 0) return;

      const row = this.notificationRows[this.currentRowIndex];
      
      // Update order status
      row.order_status = this.currentRowOrder.order_status;
      
      // Update recipient type based on checkboxes
      row.recipient_type = [...this.editRecipientTypes];
      
      // Create a new config object
      let newConfig = {};
      
      if (this.editRecipientTypes.includes('admin')) {
        newConfig.admin_mobile_numbers = this.currentRowConfig.admin_mobile_numbers;
        newConfig.admin_template_id = this.currentRowConfig.admin_template_id; // Changed name
        newConfig.admin_message = this.currentRowConfig.admin_message; // Changed name
      }
      
      if (this.editRecipientTypes.includes('customer')) {
        newConfig.customer_template_id = this.currentRowConfig.customer_template_id || '';
        newConfig.customer_message = this.currentRowConfig.customer_message || '';
      }
      
      // Update row config
      row.config = newConfig;
      
      // Close the modal
      this.closeModal();
      
      // Save settings automatically
      await this.saveSettings();
    },
  }
};
</script>

<style scoped>
.section-subtitle {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 30px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #0D0D0D;
}

.modal-body {
  overflow-y: auto;
  flex: 1;
  padding: 0 16px;
}

.modal-footer {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 16px;
}

.configuration-badge {
  display: inline-block;
  padding: 8.5px 12px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 400;
  background-color: #E5FFF0;
  border: 1px solid #0E8B51;
  color: #0E8B51;
}

.configuration-badge.not-configured {
  background-color: #FFF9E5;
  border: 1px solid #B38600;
  color: #B38600;
}



select {
  background-position: left 0.5rem center;
}
</style>